// V4-only: an alarm's location shows the device's last known fix or the alarm's own.

import Position from './models/Position';
import {mapV4LocationToV3Position} from '../containers/V4ARC/transforms';
import {LAYOUT_IFRAME_MAP, LAYOUT_MAP_PRIMARY, LAYOUT_ONLY_MAP} from '../containers/MapPage/constants';
import {findDeviceForAlarm} from './deviceMerge';

export const ALARM_LOCATION_VIEW = Object.freeze({
    LATEST: 'latest',
    AT_ALARM: 'alarm',
});

const read = (o, key) => (typeof o?.get === 'function' ? o.get(key) : o?.[key]);
const sameId = (id, other) => id != null && id !== '' && String(id) === String(other);
const fixTimeOf = (position) => +(position.fixTime || position.serverTime) || 0;

export const alarmLocationViewApplies = (user, alarm) =>
    !!(user?.isV4Only && !user.isV4Public && read(alarm, 'v4') && alarm?.isDeviceRaised);

// The embedded fix has a zero `created_at`.
export function alarmTimePosition(alarm) {
    const position = mapV4LocationToV3Position(read(alarm, 'v4Data')?.location);
    if (!position) return null;
    const raisedAt = read(alarm, 'date') || 0;
    return new Position({
        ...position,
        // Without an id a Position reads as an operating address.
        id: position.id || read(alarm, 'id'),
        serverTime: position.serverTime || raisedAt,
        deviceTime: position.deviceTime || raisedAt,
        fixTime: position.fixTime || raisedAt,
    });
}

export function fetchedLatestPosition(latest, alarm) {
    if (!latest?.position || !sameId(latest.alarmId, read(alarm, 'id'))) return null;
    const position = new Position(latest.position);
    return position.hasValidCoordinates ? position : null;
}

// `position` / `latestPosition` override the device's position; null keeps it.
// The last known fix is the newer of the device's and the alarm's.
export function resolveAlarmLocationView({user, alarm, devices, latest, selected} = {}) {
    if (!alarmLocationViewApplies(user, alarm)) {
        return {applies: false, enabled: false, view: ALARM_LOCATION_VIEW.LATEST, atAlarm: false, fetched: false, showsAlarmFix: false, position: null, latestPosition: null};
    }
    const listed = findDeviceForAlarm(devices, alarm);
    // Not the panel's cached device, which may carry the alarm's fix.
    const live = listed ? listed.position : fetchedLatestPosition(latest, alarm);
    const atAlarmPosition = alarmTimePosition(alarm);
    const liveValid = !!live?.hasValidCoordinates;
    // A fixed position is never old.
    const liveIsNewer = liveValid && !!atAlarmPosition && (!!live.fixed || fixTimeOf(live) > fixTimeOf(atAlarmPosition));
    const sameFix = liveValid && !!atAlarmPosition && fixTimeOf(live) === fixTimeOf(atAlarmPosition);
    let latestPosition;
    if (liveIsNewer || (liveValid && !atAlarmPosition)) {
        latestPosition = listed ? null : live;
    } else if (listed && sameFix) {
        latestPosition = null;
    } else {
        latestPosition = atAlarmPosition;
    }
    const enabled = liveIsNewer;
    const atAlarm = enabled && sameId(selected?.alarmId, read(alarm, 'id')) && selected?.view === ALARM_LOCATION_VIEW.AT_ALARM;
    const position = atAlarm ? atAlarmPosition : latestPosition;
    return {
        applies: true,
        enabled,
        view: atAlarm ? ALARM_LOCATION_VIEW.AT_ALARM : ALARM_LOCATION_VIEW.LATEST,
        atAlarm,
        fetched: !!latestPosition && latestPosition === live,
        showsAlarmFix: !!position && position === atAlarmPosition,
        position,
        latestPosition,
    };
}

export const coordinatesKey = (position) => `${position.latitude},${position.longitude}`;

// Only where styles.Left hides the details pane.
export const mapShowsLocationView = (layout, isPhone) =>
    [LAYOUT_IFRAME_MAP, LAYOUT_ONLY_MAP].includes(layout) || (!!isPhone && layout === LAYOUT_MAP_PRIMARY);
